import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Users, MapPin, AlertTriangle, Clock } from 'lucide-react';
import { createReservation } from '../../firebase/reservationService';
import { getAvailableTables, reserveTable, getAvailableTimeSlots, isRestaurantOpenOnDate } from '../../firebase/tableService';
import LegalTermsLink from '../../components/legal/LegalTermsLink';
import DataProtectionLink from '../../components/legal/DataProtectionLink';
import SpecialRequestForm from '../../components/SpecialRequestForm';
import SpecialConfirmation from '../../components/SpecialConfirmation';
import { RESERVATION_TYPES } from '../../firebase/reservationService';

const getPromoDataFromSession = () => {
  const promoData = sessionStorage.getItem('promoReservation');
  if (promoData) {
    try {
      return JSON.parse(promoData);
    } catch {
      return null;
    }
  }
  return null;
};

// Cache simple para mejorar rendimiento
const timeSlotCache = new Map();

const ReservationPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedPeople, setSelectedPeople] = useState(2);
  const [selectedZone, setSelectedZone] = useState(null);
  const [maxPeopleAvailable, setMaxPeopleAvailable] = useState(10);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [hasAllergies, setHasAllergies] = useState(null);
  const [specialRequestType, setSpecialRequestType] = useState(null);
  const [specialRequestCompleted, setSpecialRequestCompleted] = useState(false);
  
  // NUEVO: Estados para manejar excepciones
  const [dateException, setDateException] = useState(null);
  const [restaurantStatus, setRestaurantStatus] = useState(null);
  const [showClosureMessage, setShowClosureMessage] = useState(false);

  // Estado del formulario optimizado
  const [formData, setFormData] = useState(() => ({
    firstName: '',
    lastName: '',
    email: '',
    emailConfirm: '',
    phone: '',
    phoneConfirm: '',
    comments: '',
    allergiesDetails: ''
  }));

  const [terms, setTerms] = useState({
    conditions: false,
    privacy: false,
    marketing: false
  });

  // Zonas memorizadas para evitar recreación
  const zones = useMemo(() => [
    { id: 'salon', name: 'Salón' }
  ], []);

  // Función optimizada para obtener fechas
  const getCurrentDates = useCallback(() => {
    const dates = [];
    const today = new Date();
    for(let i = 0; i < 12; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        number: date.getDate(),
        day: ['D', 'L', 'M', 'Mi', 'J', 'V', 'S'][date.getDay()],
        available: true,
        fullDate: date
      });
    }
    return dates;
  }, []);

  const [dates] = useState(() => getCurrentDates());
  
  const handleSpecialRequest = (type) => {
    setSpecialRequestType(type);
  };
  
  const handleSpecialRequestSuccess = () => {
    setSpecialRequestCompleted(true);
    setCurrentStep(3); // Ir a la página de confirmación
  };

  // Inicializar la fecha al montar el componente
  useEffect(() => {
    const today = dates[0];
    if (today) {
      console.log('[ReservationPage] Seleccionando día actual:', today);
      handleDateSelect(today);
    }
  }, []);

  // NUEVO: Función para verificar estado del restaurante y excepciones
  const checkRestaurantStatus = useCallback(async (date) => {
    try {
      const status = await isRestaurantOpenOnDate(date);
      setRestaurantStatus(status);
      
      if (!status.isOpen) {
        setDateException({
          reason: status.exception || 'Restaurante cerrado',
          customMessage: status.customMessage || 'El restaurante está cerrado este día',
          closureType: status.closureType || 'full',
          shifts: status.shifts || []
        });
        setShowClosureMessage(true);
        setAvailableTimes([]);
        return false;
      } else {
        setDateException(null);
        setShowClosureMessage(false);
        return true;
      }
    } catch (error) {
      console.error('Error verificando estado del restaurante:', error);
      setRestaurantStatus(null);
      setDateException(null);
      setShowClosureMessage(false);
      return true; // En caso de error, asumir que está abierto
    }
  }, []);

  // Función optimizada para verificar disponibilidad
  const checkAvailability = useCallback(async (date, people) => {
    const cacheKey = `${date.toISOString()}-${people}`;
    
    // Verificar caché
    if (timeSlotCache.has(cacheKey)) {
      const { data, timestamp } = timeSlotCache.get(cacheKey);
      // Caché válido por 5 minutos
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return data;
      }
      timeSlotCache.delete(cacheKey);
    }
  
    try {
      // Asegurarnos que people es un número
      const guestsNumber = typeof people === 'number' ? people : parseInt(people);
      const tablesNeeded = Math.ceil(guestsNumber / 3);
      const slots = await getAvailableTimeSlots(date, tablesNeeded);
      
      // Guardar en caché
      timeSlotCache.set(cacheKey, {
        data: slots,
        timestamp: Date.now()
      });
      
      return slots;
    } catch (error) {
      console.error('Error al verificar disponibilidad:', error);
      return [];
    }
  }, []);

  // ACTUALIZADO: Efecto para cargar disponibilidad con verificación de excepciones
  useEffect(() => {
    if (selectedDate?.fullDate) {
      setLoading(true);
      
      // Primero verificar si el restaurante está abierto
      checkRestaurantStatus(selectedDate.fullDate)
        .then(isOpen => {
          if (isOpen) {
            // Solo si está abierto, verificar disponibilidad
            return checkAvailability(selectedDate.fullDate, selectedPeople);
          }
          return [];
        })
        .then(slots => {
          setAvailableTimes(slots);
          if (slots.length > 0) {
            const maxTables = Math.max(...slots.map(slot => slot.availableTables ?? 0));
            setMaxPeopleAvailable(Math.min(maxTables * 3, 12));
          }
        })
        .finally(() => setLoading(false));
    }
  }, [selectedDate, selectedPeople, checkAvailability, checkRestaurantStatus]);

  const handleDateSelect = useCallback((date) => {
    if (!date.available) return;
    setSelectedDate(date);
    setSelectedTime(null);
  }, []);

  const handleTimeSelect = useCallback((time) => {
    setSelectedTime(time);
  }, []);

  const handlePeopleChange = useCallback((e) => {
    setSelectedPeople(parseInt(e.target.value));
    setSelectedTime(null);
  }, []);

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  useEffect(() => {
    // Manejar datos de Google
    const googleReservationData = sessionStorage.getItem('googleReservation');
    if (googleReservationData) {
      try {
        const { date, time, guests } = JSON.parse(googleReservationData);
        
        if (date) {
          const dateObj = new Date(date);
          const matchingDate = dates.find(d => 
            d.fullDate.toDateString() === dateObj.toDateString()
          );
          if (matchingDate) {
            handleDateSelect(matchingDate);
          }
        }
        
        if (guests) {
          setSelectedPeople(guests);
        }
        
        sessionStorage.removeItem('googleReservation');
      } catch (error) {
        console.error('Error procesando datos de Google:', error);
      }
    }
    
    // Capturar parámetros de tracking de la URL
    const urlParams = new URLSearchParams(window.location.search);
    const promoCode = urlParams.get('promo');
    const source = urlParams.get('source') || urlParams.get('utm_source');
    
    if (promoCode) {
      sessionStorage.setItem('promo_code', promoCode);
      console.log('Código promocional capturado:', promoCode);
    }
    
    if (source) {
      sessionStorage.setItem('tracking_source', source);
      console.log('Origen capturado:', source);
    }
    
    // Limpiar la URL si hay parámetros de tracking (opcional)
    if (promoCode || source) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    
    // Manejar datos de promoción con parámetros adicionales
    const promoReservationData = sessionStorage.getItem('promoReservation');
    if (promoReservationData) {
      try {
        const { date, time, guests, promo, source } = JSON.parse(promoReservationData);
        
        if (promo) {
          sessionStorage.setItem('promo_code', promo);
        }
        
        if (source) {
          sessionStorage.setItem('tracking_source', source);
        }
        
        if (date) {
          const dateObj = new Date(date);
          const matchingDate = dates.find(d => 
            d.fullDate.toDateString() === dateObj.toDateString()
          );
          if (matchingDate) {
            handleDateSelect(matchingDate);
          }
        }
        
        if (guests) {
          setSelectedPeople(guests);
        }
        
        // Limpiar datos de promoción pero NO el código promo ni source
        sessionStorage.removeItem('promoReservation');
      } catch (error) {
        console.error('Error procesando datos de promoción:', error);
      }
    }
    
  }, [dates, handleDateSelect]);

  // Manejador de envío optimizado
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const availableTables = await getAvailableTables(
        selectedDate.fullDate.toISOString(),
        selectedTime
      );
      
      const guestsNumber = parseInt(selectedPeople);
      const tablesNeeded = Math.ceil(guestsNumber / 3);
      
      if (availableTables.length < tablesNeeded) {
        alert('Lo sentimos, no hay suficientes mesas disponibles para este horario.');
        return;
      }
  
      const reservedTables = [];
      for (let i = 0; i < tablesNeeded; i++) {
        const reservedUntil = new Date(selectedDate.fullDate);
        reservedUntil.setHours(parseInt(selectedTime.split(':')[0]) + 2);
        reservedUntil.setMinutes(parseInt(selectedTime.split(':')[1]));
        
        await reserveTable(availableTables[i].id, reservedUntil.toISOString());
        reservedTables.push(availableTables[i].id);
      }
      
      const isFromGoogle = sessionStorage.getItem('googleReservation') !== null;
      
      const reservationData = {
        date: formatDate(selectedDate.fullDate),
        time: selectedTime,
        guests: guestsNumber,
        zone: selectedZone.name,
        tables: reservedTables,
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          comments: formData.comments || '',
          allergies: hasAllergies ? formData.allergiesDetails : null
        },
        status: 'PENDIENTE DE CONFIRMACION',
        createdAt: new Date().toISOString()
      };
  
      await createReservation(reservationData, isFromGoogle ? 'google' : 'web');
      
      if (isFromGoogle) {
        sessionStorage.removeItem('googleReservation');
      }
      
      setCurrentStep(3);
    } catch (error) {
      console.error('Error en la reserva:', error);
      alert('Error al crear la reserva: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedTime, selectedPeople, selectedZone, formData, hasAllergies]);

  // formatDate memorizado
  const formatDate = useMemo(() => {
    return (date) => {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
  }, []);

  // NUEVO: Componente para mostrar mensaje de cierre
  const ClosureMessage = () => {
    if (!showClosureMessage || !dateException) return null;

    const getClosureIcon = () => {
      switch (dateException.closureType) {
        case 'full':
          return <Calendar className="w-12 h-12 text-red-500" />;
        case 'lunch':
          return <Clock className="w-12 h-12 text-yellow-500" />;
        case 'dinner':
          return <Clock className="w-12 h-12 text-orange-500" />;
        default:
          return <AlertTriangle className="w-12 h-12 text-blue-500" />;
      }
    };

    const getClosureTitle = () => {
      switch (dateException.closureType) {
        case 'full':
          return 'Restaurante Cerrado';
        case 'lunch':
          return 'Servicio de Comidas Cerrado';
        case 'dinner':
          return 'Servicio de Cenas Cerrado';
        default:
          return 'Horario Limitado';
      }
    };

    const getClosureDescription = () => {
      switch (dateException.closureType) {
        case 'full':
          return 'El restaurante estará cerrado todo el día.';
        case 'lunch':
          return 'Solo estará disponible el servicio de cenas (20:00 - 22:30).';
        case 'dinner':
          return 'Solo estará disponible el servicio de comidas (13:00 - 16:00).';
        default:
          if (dateException.shifts && dateException.shifts.length > 0) {
            const horariosStr = dateException.shifts.map(shift => `${shift.start} - ${shift.end}`).join(', ');
            return `Horarios disponibles: ${horariosStr}`;
          }
          return 'Horario especial para este día.';
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md mx-auto mb-8">
        <div className="flex justify-center mb-4">
          {getClosureIcon()}
        </div>
        
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          {getClosureTitle()}
        </h3>
        
        <p className="text-lg text-gray-600 mb-4">
          {formatDate(selectedDate.fullDate)}
        </p>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-gray-700 mb-2">
            {getClosureDescription()}
          </p>
          
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mt-3">
            <p className="text-blue-800 font-medium">
              {dateException.customMessage || dateException.reason}
            </p>
          </div>
        </div>
        
        <div className="text-sm text-gray-500">
          <p>Puedes seleccionar otra fecha para hacer tu reserva.</p>
          {dateException.closureType !== 'full' && (
            <p className="mt-1 font-medium text-gray-600">
              Los horarios mostrados son los únicos disponibles para este día.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <h1 className="text-center text-2xl text-gray-600 font-semibold mb-2">
            LOW COUNTRY BOYS
          </h1>
          <h2 className="text-center text-xl text-gray-600">
            LAS ROZAS
          </h2>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-12">
          {[
            { num: 1, text: 'Encontrar' },
            { num: 2, text: 'Información' },
            { num: 3, text: 'Confirmación' }
          ].map((step) => (
            <div key={step.num} className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 
                ${currentStep === step.num ? 
                  'bg-black text-white' : 
                  'bg-gray-200 text-gray-500'}`}>
                {step.num}
              </div>
              <span className={`text-sm ${
                currentStep === step.num ? 'text-black' : 'text-gray-500'
              }`}>
                {step.text}
              </span>
            </div>
          ))}
        </div>

        {currentStep === 1 && (
          <>
            {/* People Selector */}
            <div className="mb-8">
              <select
                value={selectedPeople}
                onChange={handlePeopleChange}
                className="w-full max-w-xs mx-auto block p-3 border border-gray-300 rounded-md text-gray-700"
              >
                {Array.from({ length: maxPeopleAvailable - 1 }, (_, i) => i + 2).map(num => (
                  <option key={num} value={num}>{num} personas</option>
                ))}
              </select>
            </div>

            {/* Calendar */}
            <div className="mb-12">
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {dates.map((date) => (
                  <button
                    key={date.number}
                    onClick={() => handleDateSelect(date)}
                    disabled={!date.available}
                    className={`w-12 h-12 rounded-full flex flex-col items-center justify-center
                      ${selectedDate?.number === date.number ? 
                        'bg-[#e31837] text-white' : 
                        date.available ? 
                          'hover:bg-gray-100' : 
                          'opacity-50 cursor-not-allowed'
                      }`}
                  >
                    <span className="text-sm font-bold">{date.number}</span>
                    <span className="text-xs">{date.day}</span>
                  </button>
                ))}
                <button className="w-12 h-12 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-[#e31837]" />
                </button>
              </div>
            </div>

            {/* NUEVO: Mostrar mensaje de cierre si corresponde */}
            <ClosureMessage />

            {/* Time Slots - Solo mostrar si no hay cierre completo */}
            {selectedDate && !selectedTime && !showClosureMessage && (
              <div className="mb-12">
                <h3 className="text-center text-[#e31837] font-bold mb-6">
                  HORARIOS DISPONIBLES
                  {dateException && dateException.closureType !== 'full' && (
                    <div className="text-sm text-blue-600 font-normal mt-1">
                      {dateException.customMessage || dateException.reason}
                    </div>
                  )}
                </h3>
                
                {/* Comidas */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold mb-4 text-center">Comidas</h4>
                  {(() => {
                    const lunchSlots = availableTimes.filter(slot => {
                      const hour = parseInt(slot.time.split(':')[0]);
                      return hour < 20;
                    });
                    
                    if (lunchSlots.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <div className="text-gray-500 text-lg mb-2">
                            {dateException?.closureType === 'lunch' || dateException?.closureType === 'full' ? 
                              'Cerrado por ' + (dateException.reason || 'evento especial') : 
                              'No hay horarios disponibles para comidas'
                            }
                          </div>
                          {dateException?.closureType === 'lunch' && (
                            <div className="text-sm text-gray-400">
                              Las cenas siguen disponibles
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid grid-cols-3 gap-4">
                        {lunchSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => handleTimeSelect(slot.time)}
                            disabled={!slot.available}
                            className={`py-3 px-4 text-center rounded ${
                              slot.available 
                                ? 'bg-[#e31837] text-white hover:bg-[#c41230]' 
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {slot.time}
                            {!slot.available && slot.availableTables > 0 && (
                              <div className="text-xs mt-1">
                                Máx. {slot.availableTables * 3} personas
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Cenas */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-center">Cenas</h4>
                  {(() => {
                    const dinnerSlots = availableTimes.filter(slot => {
                      const hour = parseInt(slot.time.split(':')[0]);
                      return hour >= 20;
                    });
                    
                    if (dinnerSlots.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <div className="text-gray-500 text-lg mb-2">
                            {dateException?.closureType === 'dinner' || dateException?.closureType === 'full' ? 
                              'Cerrado por ' + (dateException.reason || 'evento especial') : 
                              'No hay horarios disponibles para cenas'
                            }
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid grid-cols-3 gap-4">
                        {dinnerSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => handleTimeSelect(slot.time)}
                            disabled={!slot.available}
                            className={`py-3 px-4 text-center rounded ${
                              slot.available 
                                ? 'bg-[#e31837] text-white hover:bg-[#c41230]' 
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {slot.time}
                            {!slot.available && slot.availableTables > 0 && (
                              <div className="text-xs mt-1">
                                Máx. {slot.availableTables * 3} personas
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Zone Selection */}
            {selectedTime && !selectedZone && (
              <div className="mb-12">
                <h3 className="text-center text-[#e31837] font-bold mb-6">
                  Seleccione una zona del restaurante para reservar
                </h3>
                <div className="flex justify-center">
                  {zones.map((zone) => (
                    <button
                      key={zone.id}
                      onClick={() => {
                        setSelectedZone(zone);
                        setCurrentStep(2);
                      }}
                      className="w-48 p-4 border-2 border-[#e31837] text-[#e31837] rounded hover:bg-gray-50 transition-colors"
                    >
                      {zone.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Resto del componente - steps 2 y 3 permanecen igual */}
        {currentStep === 2 && (
          <div className="max-w-xl mx-auto">
            {/* Resumen de la reserva */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(selectedDate.fullDate)}, {selectedTime}h.</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5" />
                <span>{selectedPeople}, {selectedZone.name}.</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>Av. Ntra. Señora del Retamar, 16, 28232 Las Rozas de Madrid, Madrid.</span>
              </div>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Nombre</label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    className="w-full p-2 border rounded"
                    value={formData.firstName}
                    onChange={handleFormChange}
                    placeholder="Nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Apellidos</label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    className="w-full p-2 border rounded"
                    value={formData.lastName}
                    onChange={handleFormChange}
                    placeholder="Apellidos"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full p-2 border rounded"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Email"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Repetir email</label>
                <input
                  type="email"
                  name="emailConfirm"
                  required
                  className="w-full p-2 border rounded"
                  value={formData.emailConfirm}
                  onChange={handleFormChange}
                  placeholder="Repetir email"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Prefijo</label>
                  <select 
                    className="w-full p-2 border rounded"
                    defaultValue="+34"
                  >
                    <option value="+34">España (+34)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full p-2 border rounded"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="Teléfono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Repetir teléfono</label>
                <input
                  type="tel"
                  name="phoneConfirm"
                  required
                  className="w-full p-2 border rounded"
                  value={formData.phoneConfirm}
                  onChange={handleFormChange}
                  placeholder="Repetir teléfono"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">
                  Introduce un comentario sobre la reserva
                </label>
                <textarea
                  name="comments"
                  className="w-full p-2 border rounded"
                  rows={3}
                  value={formData.comments}
                  onChange={handleFormChange}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">
                    ¿Tiene algún comensal alguna intolerancia/alergia?
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="allergies"
                        value="yes"
                        onChange={() => setHasAllergies(true)}
                        className="mr-2"
                      />
                      Sí
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="allergies"
                        value="no"
                        onChange={() => setHasAllergies(false)}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                </div>

                {hasAllergies && (
                  <div className="mt-2">
                    <label className="block text-sm mb-1">
                      Por favor, indique las alergias o intolerancias:
                    </label>
                    <textarea
                      name="allergiesDetails"
                      className="w-full p-2 border rounded"
                      rows={3}
                      value={formData.allergiesDetails}
                      onChange={handleFormChange}
                      placeholder="Describa aquí las alergias o intolerancias"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4 mt-8">
                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      required
                      className="mt-1 mr-2"
                      checked={terms.conditions}
                      onChange={(e) => setTerms({...terms, conditions: e.target.checked})}
                    />
                    <span className="text-sm">
                      Acepto las <LegalTermsLink />
                    </span>
                  </label>
                </div>
                
                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      required
                      className="mt-1 mr-2"
                      checked={terms.privacy}
                      onChange={(e) => setTerms({...terms, privacy: e.target.checked})}
                    />
                    <span className="text-sm">
                      Consiento el <DataProtectionLink />
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 mr-2"
                      checked={terms.marketing}
                      onChange={(e) => setTerms({...terms, marketing: e.target.checked})}
                    />
                    <span className="text-sm">
                      Consiento la recepción de comunicaciones del restaurante por e-mail y/o SMS con fines comerciales
                    </span>
                  </label>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-3 bg-[#e31837] text-white rounded hover:bg-[#c41230]"
                >
                  Confirmar Reserva
                </button>
              </div>
            </form>
          </div>
        )}

        {currentStep === 3 && (
          <div className="max-w-2xl mx-auto px-4">
            {/* Icono de confirmación */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 rounded-full bg-yellow-300 flex items-center justify-center">
                <svg className="w-12 h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            {specialRequestCompleted ? (
              <SpecialConfirmation 
                type={specialRequestType}
                customerName={`${formData.firstName} ${formData.lastName}`}
                date={selectedDate ? formatDate(selectedDate.fullDate) : ''}
                people={selectedPeople}
              />
            ) : (
              <>
                <h2 className="text-2xl font-bold text-center mb-8">
                  Reserva confirmada
                </h2>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>{formatDate(selectedDate.fullDate)}, {selectedTime}h.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    <span>{selectedPeople} personas, {selectedZone.name}.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span>Av. Ntra. Señora del Retamar, 16, 28232 Las Rozas de Madrid, Madrid.</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="font-bold">
                    Reserva a nombre de: {formData.firstName} {formData.lastName}
                  </p>
                  
                  {formData.comments && (
                    <p className="text-gray-600">
                      {formData.comments}
                    </p>
                  )}

                  <p>Gracias por reservar en Low Country Boys Las Rozas.</p>
                  <p>Recibirá en breve un email de confirmación.</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Lista de espera y Grupo grande - Solo visible en paso 1 y si no hay cierre completo */}
        {currentStep === 1 && !selectedTime && !showClosureMessage && (
          <div className="space-y-4 max-w-lg mx-auto mt-8">
            <button 
              className="w-full py-3 px-4 border-2 border-[#e31837] text-[#e31837] rounded hover:bg-gray-50 transition-colors"
              onClick={() => handleSpecialRequest(RESERVATION_TYPES.WAITLIST)}
            >
              Lista de espera
            </button>
            <button 
              className="w-full py-3 px-4 border-2 border-[#e31837] text-[#e31837] rounded hover:bg-gray-50 transition-colors"
              onClick={() => handleSpecialRequest(RESERVATION_TYPES.GROUP)}
            >
              Solicitud de grupo (A partir de 12 pax )
            </button>
          </div>
        )}
      </div>

      {/* Modal de solicitud especial */}
      {specialRequestType && !specialRequestCompleted && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="my-8">
            <SpecialRequestForm 
              type={specialRequestType}
              selectedDate={selectedDate}
              selectedPeople={selectedPeople}
              onSuccess={handleSpecialRequestSuccess}
              onCancel={() => setSpecialRequestType(null)}
            />
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationPage;